// Selection rings under units: a thin one under the hero you control, a faint one under whatever the
// cursor is over, a stronger one under your current attack target. They sit just outside the body
// (HERO_R, MONSTER_LOOK.ring); fx.ts keeps its status rings at 1.15 m and beyond so the two never read as one.
import * as THREE from 'three';
import { mk, flat, release, TEAM } from './engrave.js';
import { isMonsterUnit, isSummonUnit, ringOf, type Unit } from './input.js';
import type { GameView, ViewHero, ViewMonster } from './gameView.js';

const HERO_R = 0.82;
const WIDTH = { own: 0.07, hover: 0.05, focus: 0.13 };

/* A flat annulus; `dashed` keeps every other segment (RingGeometry emits 6 indices per segment). */
function ring(inner: number, outer: number, segments: number, dashed: boolean) {
  const g = new THREE.RingGeometry(inner, outer, segments, 1);
  if (dashed) {
    const all = g.index!.array, keep: number[] = [];
    for (let i = 0; i < segments; i += 2) for (let k = 0; k < 6; k++) keep.push(all[i * 6 + k]!);
    g.setIndex(keep);
  }
  return g;
}

export type RingPicks = { own: ViewHero | null; hover: Unit | null; focus: Unit | null };

/** Which unit wears which ring. `shown` is "alive and visible to the viewer". The own hero only ever
    wears its own ring; a focused unit under the cursor keeps the focus ring. Writes into `out`. */
export function pickRings(out: RingPicks, self: ViewHero | null, hovered: Unit | null, focus: Unit | null, shown: (u: Unit) => boolean): RingPicks {
  out.own = self && self.alive ? self : null;
  out.focus = focus && focus !== self && shown(focus) ? focus : null;
  out.hover = hovered && hovered !== self && hovered !== out.focus && shown(hovered) ? hovered : null;
  return out;
}

export function createUnitRings(scene: THREE.Scene, game: GameView) {
  const team = game.player!.team;
  const geos: Record<string, THREE.RingGeometry> = {};
  // `r` null: a hero's own ring.
  const geo = (kind: 'own' | 'hover' | 'focus', r: number | null) => {
    const radius = r ?? HERO_R;
    return geos[kind + radius] ??= ring(radius - WIDTH[kind], radius, radius < 1.2 ? 48 : 96, kind === 'hover');
  };
  const make = (m: THREE.Mesh, y: number) => { const f = flat(m, y); f.visible = false; scene.add(f); return f; };
  const own = make(mk(geo('own', null), TEAM[team], { team, cast: false }) as THREE.Mesh, 0.26);
  // Hover: a dashed hairline, tentative the way a pencil guide is; any solid thin line on this renderer
  // comes out as a full-weight contour. Focus: a heavier stippled carmine line, neutral to either team.
  const hover = make(mk(geo('hover', null), 0xa88c62, { team: 'unit', cast: false }) as THREE.Mesh, 0.27);
  const focus = make(mk(geo('focus', null), 0xa8322a, { fx: true, cast: false }) as THREE.Mesh, 0.275);
  const picks: RingPicks = { own: null, hover: null, focus: null };
  let monsters: readonly ViewMonster[] = [];
  const shown = (u: Unit) => (isMonsterUnit(u) ? monsters.includes(u) : isSummonUnit(u) ? game.summons.includes(u) : u.alive && game.visibleTo(team, u));

  function place(m: THREE.Mesh, kind: 'hover' | 'focus', u: Unit | null) {
    m.visible = !!u;
    if (!u) return;
    m.geometry = geo(kind, isMonsterUnit(u) || isSummonUnit(u) ? ringOf(u) : null);
    m.position.set(u.pos.x, m.position.y, u.pos.z);
  }

  /** `seen` is this frame's read of game.monsters, shared with input.update(). */
  function update(hovered: Unit | null, target: Unit | null, seen: readonly ViewMonster[]) {
    monsters = seen;
    pickRings(picks, game.player ?? null, hovered, target, shown);
    own.visible = !!picks.own;
    if (picks.own) own.position.set(picks.own.pos.x, own.position.y, picks.own.pos.z);
    place(hover, 'hover', picks.hover);
    place(focus, 'focus', picks.focus);
  }
  function destroy() {
    for (const m of [own, hover, focus]) { scene.remove(m); release(m, { geometry: false }); }
    for (const g of Object.values(geos)) g.dispose();
  }
  return { update, destroy };
}
